import React, { useState, useRef } from "react";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { db, storage, functions } from "../../firebase/config";
import { uploadContractorDocument } from "../../firebase/firestore";
import { getDocumentVersions } from "./helpers";

// Contractor documents modal: signed agreement, W-9, Direct Deposit —
// secure downloads via short-lived signed URLs, admin uploads.
const DocumentsModal = ({ submission, onClose, showToast, onSubmissionChange, currentAdminEmail }) => {
    const [downloadingDoc, setDownloadingDoc] = useState(null);
    const [uploadingDoc, setUploadingDoc] = useState(null);
    const [deletingDoc, setDeletingDoc] = useState(null);
    const adminFileInputRef = useRef(null);
    const adminUploadTargetRef = useRef(null);

    // Secure document download using Cloud Function signed URLs.
    // Pass a storagePath to download a specific version.
    const handleSecureDownload = async (applicationId, documentType, storagePath = null) => {
        setDownloadingDoc(storagePath || documentType);
        try {
            const getSecureUrl = httpsCallable(functions, 'getSecureDocumentUrl');
            const result = await getSecureUrl({ applicationId, documentType, storagePath });

            if (result.data.success) {
                // Open the signed URL in a new tab
                window.open(result.data.url, '_blank');
            }
        } catch (error) {
            console.error("Error getting secure download URL:", error);
            alert(`Failed to download document: ${error.message}`);
        } finally {
            setDownloadingDoc(null);
        }
    };

    // Permanently delete a document (W-9/DD version or the signed agreement)
    // via the admin-only Cloud Function — removes the storage file, fixes the
    // record's version list/pointers, and logs the deletion to the activity log.
    const handleDeleteDocument = async (documentType, version = null) => {
        const isAgreement = documentType === 'agreement';
        const fileLabel = isAgreement
            ? `the signed contractor agreement${submission.agreementFileName ? ` ("${submission.agreementFileName}")` : ''}`
            : `"${version?.fileName || 'this file'}"`;

        const confirmed = window.confirm(
            `Permanently delete ${fileLabel}?\n\nThe file will be removed from storage and cannot be recovered.`
        );
        if (!confirmed) return;

        const deleteKey = isAgreement ? 'agreement' : version?.storagePath;
        setDeletingDoc(deleteKey);
        const recordId = submission.id;
        try {
            const deleteDocument = httpsCallable(functions, 'deleteContractorDocument');
            await deleteDocument({
                applicationId: recordId,
                documentType,
                storagePath: isAgreement ? null : version?.storagePath,
                adminEmail: currentAdminEmail,
            });

            onSubmissionChange(prev => {
                if (!prev || prev.id !== recordId) return prev;
                if (isAgreement) {
                    const {
                        signedPdfUrl, signedPdfPath, agreementFileName,
                        agreementUploadedManually, agreementUploadedAt, agreementUploadedBy,
                        ...rest
                    } = prev;
                    return rest;
                }
                const remaining = getDocumentVersions(prev, documentType)
                    .filter(v => v.storagePath !== version.storagePath);
                const documents = { ...prev.documents };
                if (!documents[documentType] || documents[documentType].storagePath === version.storagePath) {
                    if (remaining.length) {
                        documents[documentType] = remaining[remaining.length - 1];
                    } else {
                        delete documents[documentType];
                    }
                }
                return {
                    ...prev,
                    documents,
                    documentVersions: { ...prev.documentVersions, [documentType]: remaining },
                };
            });

            showToast('Document deleted');
        } catch (error) {
            console.error('Error deleting document:', error);
            showToast(`Failed to delete: ${error.message}`, 'error');
        } finally {
            setDeletingDoc(null);
        }
    };

    // Admin upload: trigger file picker for a specific document type
    const triggerAdminUpload = (documentType) => {
        adminUploadTargetRef.current = documentType;
        adminFileInputRef.current.value = '';
        adminFileInputRef.current.click();
    };

    // Admin upload: manually uploaded signed contractor agreement
    // (e.g. paper contracts signed outside the website). Uses a fresh
    // timestamped path so the previous file is never overwritten.
    const handleAgreementUpload = async (file) => {
        if (submission.signedPdfUrl) {
            const confirmed = window.confirm(
                `This contractor already has a signed agreement on file.\n\nReplace it with "${file.name}"?\n\n(The previous file stays in storage.)`
            );
            if (!confirmed) return;
        }

        setUploadingDoc('agreement');
        const recordId = submission.id;
        try {
            const storagePath = `signed-agreements/manual_agreement_${recordId}_${Date.now()}.pdf`;
            const storageRef = ref(storage, storagePath);
            await uploadBytes(storageRef, file);
            const downloadUrl = await getDownloadURL(storageRef);

            const activityEntry = {
                id: Date.now().toString(),
                type: "note",
                category: "general",
                timestamp: new Date(),
                adminEmail: currentAdminEmail,
                content: `Manually uploaded signed contractor agreement ("${file.name}").`,
            };

            await updateDoc(doc(db, "medical_providers", recordId), {
                signedPdfUrl: downloadUrl,
                signedPdfPath: storagePath,
                agreementFileName: file.name,
                agreementUploadedManually: true,
                agreementUploadedAt: new Date(),
                agreementUploadedBy: currentAdminEmail,
                activityLog: arrayUnion(activityEntry),
            });

            onSubmissionChange(prev => {
                if (!prev || prev.id !== recordId) return prev;
                return {
                    ...prev,
                    signedPdfUrl: downloadUrl,
                    signedPdfPath: storagePath,
                    agreementFileName: file.name,
                    agreementUploadedManually: true,
                    agreementUploadedAt: new Date(),
                    agreementUploadedBy: currentAdminEmail,
                    activityLog: [...(prev.activityLog || []), activityEntry],
                };
            });

            showToast('Signed agreement uploaded');
        } catch (error) {
            console.error('Error uploading signed agreement:', error);
            showToast(`Failed to upload agreement: ${error.message}`, 'error');
        } finally {
            setUploadingDoc(null);
            adminUploadTargetRef.current = null;
        }
    };

    // Admin upload: handle file selection
    const handleAdminFileSelected = async (e) => {
        const file = e.target.files[0];
        const documentType = adminUploadTargetRef.current;
        if (!file || !documentType || !submission) return;

        if (file.type !== 'application/pdf') {
            showToast('Only PDF files are allowed', 'error');
            return;
        }

        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            showToast('File must be less than 10MB', 'error');
            return;
        }

        if (documentType === 'agreement') {
            await handleAgreementUpload(file);
            return;
        }

        const label = documentType === 'w9' ? 'W-9' : 'Direct Deposit';

        setUploadingDoc(documentType);
        const recordId = submission.id;
        try {
            // Additive: every upload is kept as a new version
            const currentVersions = getDocumentVersions(submission, documentType);
            const result = await uploadContractorDocument(recordId, documentType, file, {
                uploadedBy: currentAdminEmail,
                currentVersions,
            });

            onSubmissionChange(prev => {
                if (!prev || prev.id !== recordId) return prev;
                return {
                    ...prev,
                    documents: {
                        ...prev.documents,
                        [documentType]: {
                            fileName: result.versionEntry.fileName,
                            storagePath: result.versionEntry.storagePath,
                            uploadedAt: result.versionEntry.uploadedAt,
                            fileSize: result.versionEntry.fileSize,
                        },
                    },
                    documentVersions: {
                        ...prev.documentVersions,
                        [documentType]: [...currentVersions, result.versionEntry],
                    },
                };
            });

            showToast(`${label} uploaded successfully`);
        } catch (error) {
            console.error(`Error uploading ${documentType}:`, error);
            showToast(`Failed to upload ${label}: ${error.message}`, 'error');
        } finally {
            setUploadingDoc(null);
            adminUploadTargetRef.current = null;
        }
    };

    const formatUploadedAt = (uploadedAt) => {
        if (!uploadedAt) return 'Unknown';
        return uploadedAt.toDate ? uploadedAt.toDate().toLocaleString() : new Date(uploadedAt).toLocaleString();
    };

    // W-9 / Direct Deposit section with the full version list — contractors can
    // have multiple of each (e.g. several billing entities); uploads are additive.
    const renderVersionedDocument = (documentType, label, uploadLabel) => {
        const versions = getDocumentVersions(submission, documentType);
        return (
            <div className={`document-item ${versions.length ? 'uploaded' : 'missing'}`}>
                <div className="document-item-header">
                    <div className="document-title">
                        <span className="document-icon">{versions.length ? '✅' : '❌'}</span>
                        <h4>{label}{versions.length > 1 ? ` (${versions.length} on file)` : ''}</h4>
                    </div>
                    <button
                        className="admin-upload-btn"
                        onClick={() => triggerAdminUpload(documentType)}
                        disabled={uploadingDoc === documentType}
                        title={versions.length ? "Add another — existing files are kept" : undefined}>
                        {uploadingDoc === documentType ? '⏳ Uploading...' : uploadLabel}
                    </button>
                </div>
                {versions.length === 0 ? (
                    <p className="document-missing">Not uploaded yet</p>
                ) : (
                    <div className="document-version-list">
                        {[...versions].reverse().map((version, idx) => (
                            <div key={version.storagePath || idx} className="document-version-row">
                                <div className="document-version-info">
                                    <p className="document-filename" title={version.fileName}>{version.fileName}</p>
                                    <p className="document-date">
                                        {formatUploadedAt(version.uploadedAt)}
                                        {version.uploadedBy ? ` · ${version.uploadedBy.split('@')[0]}` : ''}
                                    </p>
                                </div>
                                <div className="document-version-actions">
                                    <button
                                        className="secure-download-btn"
                                        onClick={() => handleSecureDownload(submission.id, documentType, version.storagePath)}
                                        disabled={downloadingDoc === (version.storagePath || documentType)}>
                                        {downloadingDoc === (version.storagePath || documentType) ? '⏳' : '⬇️'} Download
                                    </button>
                                    <button
                                        className="doc-delete-btn"
                                        onClick={() => handleDeleteDocument(documentType, version)}
                                        disabled={deletingDoc === version.storagePath}
                                        title="Permanently delete this file">
                                        {deletingDoc === version.storagePath ? '⏳' : '🗑️'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div
            className="modal-overlay"
            onClick={onClose}>
            <div
                className="modal-content documents-modal"
                onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>📁 Contractor Documents</h2>
                    <button
                        className="close-btn"
                        onClick={onClose}>
                        ×
                    </button>
                </div>
                <div className="modal-body">
                    <div className="contractor-info-banner">
                        <strong>{submission.name}</strong>
                        <span>{submission.email}</span>
                    </div>

                    <input
                        type="file"
                        ref={adminFileInputRef}
                        onChange={handleAdminFileSelected}
                        accept="application/pdf"
                        style={{ display: 'none' }}
                    />

                    <div className="documents-list">
                        {/* Signed Agreement */}
                        <div className={`document-item ${submission.signedPdfUrl ? 'uploaded' : 'missing'}`}>
                            <div className="document-item-header">
                                <div className="document-title">
                                    <span className="document-icon">{submission.signedPdfUrl ? '✅' : '❌'}</span>
                                    <h4>Signed Contractor Agreement</h4>
                                </div>
                                <button
                                    className="admin-upload-btn"
                                    onClick={() => triggerAdminUpload('agreement')}
                                    disabled={uploadingDoc === 'agreement'}
                                    title="Upload an executed contract (e.g. a paper agreement signed outside the website)">
                                    {uploadingDoc === 'agreement' ? '⏳ Uploading...' : '📤 Upload Contract'}
                                </button>
                            </div>
                            {submission.signedPdfUrl ? (
                                <div className="document-version-list">
                                    <div className="document-version-row">
                                        <div className="document-version-info">
                                            {submission.agreementUploadedManually ? (
                                                <>
                                                    <p className="document-filename" title={submission.agreementFileName}>{submission.agreementFileName}</p>
                                                    <p className="document-date">
                                                        {submission.agreementUploadedAt?.toDate
                                                            ? submission.agreementUploadedAt.toDate().toLocaleString()
                                                            : submission.agreementUploadedAt
                                                                ? new Date(submission.agreementUploadedAt).toLocaleString()
                                                                : 'Unknown'}
                                                        {submission.agreementUploadedBy ? ` · ${submission.agreementUploadedBy.split('@')[0]}` : ''}
                                                    </p>
                                                </>
                                            ) : (
                                                <>
                                                    <p className="document-filename">Signed agreement</p>
                                                    <p className="document-date">
                                                        Signed: {submission.timestamp?.toDate
                                                            ? submission.timestamp.toDate().toLocaleString()
                                                            : 'Unknown'}
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                        <div className="document-version-actions">
                                            <a
                                                href={submission.signedPdfUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="secure-download-btn">
                                                ⬇️ Download
                                            </a>
                                            <button
                                                className="doc-delete-btn"
                                                onClick={() => handleDeleteDocument('agreement')}
                                                disabled={deletingDoc === 'agreement'}
                                                title="Permanently delete the signed agreement">
                                                {deletingDoc === 'agreement' ? '⏳' : '🗑️'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="document-missing">Not signed yet</p>
                            )}
                        </div>

                        {/* W-9 Documents (versioned — every upload is kept) */}
                        {renderVersionedDocument('w9', 'W-9 Form', '📤 Upload W-9')}

                        {/* Direct Deposit Documents (versioned — every upload is kept) */}
                        {renderVersionedDocument('directDeposit', 'Direct Deposit Form', '📤 Upload DD')}
                    </div>

                    <div className="security-notice-admin">
                        🔒 Documents are stored securely and download links expire after 15 minutes.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentsModal;
